import { useEffect, useState } from "react";
import { api, formatTRY } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CustomerPicker } from "@/components/sales/CustomerPicker";
import { toast } from "sonner";

const PAYMENTS = ["Nakit", "Kredi Kartı", "Karma", "Personel"];
const num = (v) => Math.max(0, parseFloat(String(v).replace(",", ".")) || 0);

export const SaleEditDialog = ({ sale, onClose, onSaved }) => {
  const [customer, setCustomer] = useState("");
  const [memberId, setMemberId] = useState(null);
  const [payment, setPayment] = useState("Nakit");
  const [cash, setCash] = useState("");
  const [card, setCard] = useState("");
  const [prices, setPrices] = useState([]);
  const [total, setTotal] = useState("");
  const [totalTouched, setTotalTouched] = useState(false);

  useEffect(() => {
    if (!sale) return;
    setCustomer(sale.customer_name || ""); setMemberId(sale.club_member_id || null);
    setPayment(sale.payment_type || "Nakit");
    setCash(String(sale.payments?.["Nakit"] ?? "")); setCard(String(sale.payments?.["Kredi Kartı"] ?? ""));
    setPrices(sale.items.map((i) => String(i.unit_price)));
    setTotal(String(sale.total)); setTotalTouched(false);
  }, [sale]);

  if (!sale) return null;
  const subtotal = sale.items.reduce((a, it, i) => a + num(prices[i]) * it.quantity, 0);
  const grand = payment === "Personel" ? 0 : totalTouched ? num(total) : Math.max(0, subtotal - (sale.discount_amount || 0));
  const splitOk = payment !== "Karma" || Math.abs(num(cash) + num(card) - grand) < 0.01;
  const onCash = (v) => { setCash(v); setCard(String(Math.max(0, +(grand - num(v)).toFixed(2)))); };
  const onCard = (v) => { setCard(v); setCash(String(Math.max(0, +(grand - num(v)).toFixed(2)))); };
  const onPrice = (i, v) => { const p = [...prices]; p[i] = v; setPrices(p); setTotalTouched(false); };

  const save = async () => {
    if (!splitOk) return toast.error("Nakit + Kart toplamı fiş tutarına eşit olmalı");
    try {
      await api.patch(`/sales/${sale.id}`, {
        customer_name: customer, club_member_id: memberId, payment_type: payment,
        cash_amount: payment === "Karma" ? num(cash) : 0, card_amount: payment === "Karma" ? num(card) : 0,
        unit_prices: prices.map(num), total: grand,
      });
      toast.success("Satış güncellendi"); onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Güncelleme başarısız"); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="sale-edit-dialog" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Fişi Düzenle</DialogTitle>
          <DialogDescription>{new Date(sale.created_at).toLocaleString("tr-TR", { timeZone: "Europe/Istanbul" })} · Stok etkilenmez</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs">Müşteri</Label>
            <div className="mt-1"><CustomerPicker value={customer} memberId={memberId} onChange={(n, id) => { setCustomer(n); setMemberId(id); }} /></div>
          </div>
          <div>
            <Label className="text-xs">Ürün Fiyatları (birim ₺)</Label>
            <div className="mt-1 space-y-1.5" data-testid="sale-edit-items">
              {sale.items.map((it, i) => (
                <div key={`${it.recipe_id}-${it.flavor || ""}-${i}`} className="flex items-center gap-2 rounded-lg bg-secondary/50 px-3 py-1.5">
                  <span className="flex-1 truncate text-sm">{it.product_name} <span className="text-xs text-muted-foreground">x{it.quantity}</span></span>
                  <Input type="number" inputMode="decimal" min="0" step="0.01" data-testid={`sale-edit-price-${i}`} value={prices[i] ?? ""} onChange={(e) => onPrice(i, e.target.value)} className="h-8 w-28" />
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Ödeme Tipi</Label>
              <Select value={payment} onValueChange={setPayment}>
                <SelectTrigger className="mt-1" data-testid="sale-edit-payment"><SelectValue /></SelectTrigger>
                <SelectContent>{(sale.payment_type === "Açık Hesap" ? ["Açık Hesap", ...PAYMENTS] : PAYMENTS).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Fiş Toplamı (₺)</Label>
              <Input type="number" inputMode="decimal" min="0" step="0.01" data-testid="sale-edit-total" value={totalTouched ? total : grand.toFixed(2)}
                onChange={(e) => { setTotal(e.target.value); setTotalTouched(true); }} className="mt-1 font-mono" />
              <p className="mt-1 text-xs text-muted-foreground">Ara toplam {formatTRY(subtotal)}{subtotal - grand > 0.005 ? ` · indirim −${formatTRY(subtotal - grand)}` : ""}</p>
            </div>
          </div>
          {payment === "Karma" && (
            <div className="grid grid-cols-2 gap-2 rounded-lg border border-border bg-background/50 p-3" data-testid="sale-edit-split">
              <div><Label className="text-xs">Nakit (₺)</Label><Input type="number" step="0.01" data-testid="sale-edit-cash" value={cash} onChange={(e) => onCash(e.target.value)} className="mt-1 h-8" /></div>
              <div><Label className="text-xs">Kredi Kartı (₺)</Label><Input type="number" step="0.01" data-testid="sale-edit-card" value={card} onChange={(e) => onCard(e.target.value)} className="mt-1 h-8" /></div>
              {!splitOk && <p className="col-span-2 text-xs text-destructive">Toplam {formatTRY(grand)} olmalı</p>}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" data-testid="sale-edit-cancel" onClick={onClose}>Vazgeç</Button>
          <Button data-testid="sale-edit-save" onClick={save} disabled={!splitOk} className="font-semibold">Kaydet</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
